import React from 'react';

import { FormError } from './FormError';
import { Button } from './ui/button';

interface SaveBarProps {
  /** From useRecordChanges. 0 renders nothing: the bar is shown only while dirty. */
  changeCount: number;
  /** Puts the form back. No confirmation: pressing Discard is the decision. */
  onDiscard: () => void;
  onSave: () => void;
  isSaving: boolean;
  /** A change that can't be sent yet (an emptied required name). */
  saveDisabled?: boolean;
  /** Submit failure, pinned at the top of the bar (FormError). */
  error?: string | null;
}

/**
 * The record workbench's footer (DESIGN_SYSTEM.md §5 Record workbench):
 * "{n} unsaved change(s) · Discard · Save changes", present only while the
 * form differs from what the server last confirmed. The host dialog passes
 * `<Dialog pending={isSaving}>`, so nothing closes mid-save.
 *
 * Record workbenches only. Create dialogs and wizards keep their ordinary
 * footer and gate on validity.
 */
export const SaveBar: React.FC<SaveBarProps> = ({
  changeCount,
  onDiscard,
  onSave,
  isSaving,
  saveDisabled = false,
  error = null,
}) => {
  if (changeCount === 0) return null;
  return (
    <div className="shrink-0 space-y-3 border-t px-6 py-3" data-save-bar="">
      <FormError message={error} />
      <div className="flex items-center justify-between gap-3">
        <p role="status" className="text-xs text-muted-foreground">
          {changeCount} unsaved {changeCount === 1 ? 'change' : 'changes'}
        </p>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={onDiscard} disabled={isSaving}>
            Discard
          </Button>
          <Button type="button" onClick={onSave} loading={isSaving} disabled={saveDisabled}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};
